import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Glide from "@glidejs/glide";
import { DocumentContext } from "~context/DocumentContext";
import Arrow from "~components/svg/Arrow";

const Carousel = ({
  bulletColor,
  className,
  items,
  keyPrefix,
  onUpdate,
  withArrows,
  withBullets,
  gap,
  peek,
  perView,
  type,
  bound,
  rewind
}) => {
  const { windowWidth } = useContext(DocumentContext);

  const glideRef = useRef();

  const [activeIndex, setActiveIndex] = useState(0);
  const [glide, setGlide] = useState(null);

  //

  const carouselNext = () => {
    if (!glide) {
      return;
    }

    glide.go(`<`);
  };

  const carouselPrev = () => {
    if (!glide) {
      return;
    }

    glide.go(`>`);
  };

  // useEffect(() => {
  // TODO : stop memory leak
  // }, []);

  useEffect(() => {
    if (!glideRef?.current || !windowWidth || glide) {
      return;
    }
    const glideCarousel = new Glide(`#${keyPrefix}-glide`, {
      autoplay: 6000,
      gap,
      peek,
      hoverpause: true,
      perView,
      type,
      bound,
      rewind
    });

    if (type === `slider`) {
      glideCarousel.on(`run.before`, event => {
        const scrollSteps = perView;
        if (event.direction === `>`) {
          event.steps = -scrollSteps;
        } else if (event.direction === `<`) {
          event.steps = scrollSteps;
        }
      });
    }

    glideCarousel.on(`run`, () => {
      setActiveIndex(glideCarousel.index);

      onUpdate(glideCarousel.index);
    });

    glideCarousel.on(`swipe.start`, () => {
      glideCarousel.update({
        autoplay: false
      });
    });

    glideCarousel.mount();

    setGlide(glideCarousel);
  }, [glideRef, windowWidth]);

  const getIsActive = (index, isLast) => {
    if (type !== `slider`) {
      return index === activeIndex;
    }

    const start = index;
    const end = isLast
      ? items.length
      : Math.min(index + perView, items.length - perView);

    return activeIndex >= start && activeIndex < end;
  };

  const bulletCount =
    type === `slider` ? Math.ceil(items.length / perView) : items.length;

  return (
    <section
      key={`${keyPrefix}-container`}
      className={`carousel ${className ||
        `w-full relative`} flex items-center justify-center overflow-x-hidden`}
    >
      {withArrows && (
        <button
          type="button"
          className="carousel__arrow carousel__arrow--left relative pt-v1 pb-v1 pl-v1 flex items-center justify-start"
          onClick={carouselNext}
        >
          <Arrow className="relative block" color="no-milk" direction="<" />
        </button>
      )}

      <div className="carousel__glide w-full relative block">
        <div id={`${keyPrefix}-glide`} ref={glideRef} className="glide">
          <div className="glide__track" data-glide-el="track">
            <ul className="glide__slides">
              {items.map((itemJSX, itemIndex) => {
                const key = `${keyPrefix}-item-${itemIndex}`;

                return (
                  <li key={key} className="glide__slide relative">
                    {itemJSX}
                  </li>
                );
              })}
            </ul>
          </div>

          {withBullets && (
            <div
              className="glide__bullets relative xs:mt-6 flex items-center justify-center"
              data-glide-el="controls[nav]"
            >
              {Array.from(Array(bulletCount)).map((_, i) => {
                const isLast = i === bulletCount - 1;
                const bulletKey = `${keyPrefix}-bullet-${i}`;
                const slideIndex =
                  type === `slider`
                    ? Math.min(i * perView, items.length - perView)
                    : i;

                return (
                  <button
                    key={bulletKey}
                    type="button"
                    className={`glide__bullet ${
                      getIsActive(slideIndex, isLast) ? `active` : `opacity-50`
                    } bg-${bulletColor}`}
                    data-glide-dir={`=${slideIndex}`}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      {withArrows && (
        <button
          type="button"
          className="carousel__arrow carousel__arrow--right relative pt-v1 pr-v1 pb-v1 flex items-center justify-end"
          onClick={carouselPrev}
        >
          <Arrow className="relative block" color="no-milk" direction=">" />
        </button>
      )}
    </section>
  );
};

Carousel.defaultProps = {
  bulletColor: `white`,
  className: ``,
  keyPrefix: `glide-carousel`,
  onUpdate: () => {},
  withArrows: false,
  withBullets: false,
  gap: 0,
  peek: 0,
  perView: 1,
  type: `carousel`,
  bound: false,
  rewind: true
};

Carousel.propTypes = {
  bulletColor: PropTypes.string,
  className: PropTypes.string,
  items: PropTypes.node.isRequired,
  keyPrefix: PropTypes.string,
  onUpdate: PropTypes.func,
  withArrows: PropTypes.bool,
  withBullets: PropTypes.bool,
  gap: PropTypes.number,
  peek: PropTypes.number,
  perView: PropTypes.number,
  type: PropTypes.string,
  bound: PropTypes.bool,
  rewind: PropTypes.bool
};

export default Carousel;
